import styles from './styles/Show.module.scss';
import useScreenSize from 'hooks/useScreenSize';
import PageLayout from '@/components/PageLayout';
import { ClaimableEntityType, SchoolTypeEnum, SchoolsShowQuery, SchoolsShowQueryVariables } from 'queries';
import NewsFeed from '@/components/modules/NewsFeed';
import ContentSection, {
  ContentSectionFlushContent,
  ContentSectionFooterButton,
  ContentSectionHeader,
  ContentSectionHeaderLink
} from '@/components/ContentSection';
import Table, { TableCell, TableHeaderCell, TableLinkRow } from '@/components/Table';
import GenderSportIcon from '@/components/GenderSportIcon';
import ApplicationShareButton from 'containers/ApplicationContainer/components/ApplicationShareButton';
import AttributeRow from '../../../@/components/AttributeRow';
import compact from 'helpers/compact';
import AttributeRowColors from '@/components/AttributeRowColors';
import AttributeRowClaimed from '@/components/AttributeRowClaimed';
import AttributeRowImage from '@/components/AttributeRowImage';
import { DEFAULT_SCHOOL_COVER_IMAGE } from 'constants.js.erb';
import AttributeRowFollowers from '@/components/AttributeRowFollowers';
import { entryPoint } from 'react_rails/entryPoint';
import Page from '@/components/Page';
import SchoolSubNav from '@/components/subNavs/SchoolSubNav';
import TextLink, { TextLinkSize } from '@/components/TextLink';
import { SectionHeaderSize } from '@/components/SectionHeader';
import CalloutModule from '@/components/modules/CalloutModule';
import NFHSButton from '@/components/affiliates/NFHSButton';
import GoFanButton from '@/components/affiliates/GoFanButton';
import filterArticles from 'helpers/filterArticles';
import MinuteMediaAd from '@/components/ads/MinuteMediaAd';
import StnVideoAd from '@/components/ads/StnVideoAd';
import OutbrainAd from '@/components/ads/OutbrainAd';
import { selectTypename } from '@sblive-sports/api-client-js';
import PhotosCalloutModule from '@/components/modules/PhotosCalloutModule';
import Button, { ButtonSize, ButtonVariant, ButtonWidth } from '@/components/Button';

export default entryPoint(function Show({ query: { school } }: { query: SchoolsShowQuery; variables: SchoolsShowQueryVariables }) {
  const { isSmallScreen } = useScreenSize();

  const articles = filterArticles(school.articles.nodes);
  const photoGalleries = school.photoGalleries.nodes.filter(selectTypename('ContentItemPhotoGallery'));

  const schoolInfo = (
    <ContentSection>
      <ContentSectionHeader size={articles.length === 0 ? SectionHeaderSize.LARGE : undefined} right={<ApplicationShareButton />}>
        School Info
      </ContentSectionHeader>
      <AttributeRowImage src={school.coverImage || DEFAULT_SCHOOL_COVER_IMAGE} alt={school.name} />
      <AttributeRowClaimed isClaimed={school.isOwned} entityType={ClaimableEntityType.School} entityId={school.id} />
      {school.fansCount > 0 && <AttributeRowFollowers count={school.fansCount} />}
      {school.athleticDirector && <AttributeRow title="Athletic Director">{school.athleticDirector.name}</AttributeRow>}
      {school.locationText && <AttributeRow title="Location">{school.locationText}</AttributeRow>}
      {school.schoolTypeEnum && (
        <AttributeRow title="School Type">{school.schoolTypeEnum === SchoolTypeEnum.PRIVATE_SCHOOL ? 'Private' : 'Public'}</AttributeRow>
      )}
      {(school.primaryColor || school.secondaryColor) && (
        <AttributeRowColors colors={[school.primaryColor, school.secondaryColor].filter(compact)} />
      )}
    </ContentSection>
  );

  return (
    <Page subNav={<SchoolSubNav school={school} />}>
      <PageLayout
        sidebar={
          <>
            <MinuteMediaAd placement="SIDEBAR" />
            {(school.nfhsNetworkUrl || school.gofanTicketingUrl) && (
              <CalloutModule
                title="Catch All The Action"
                entity={school}
                bottomText={`Cheer on ${school.mascot ? `the ${school.mascot}` : school.name} all season long!`}
                footer={
                  <>
                    {school.nfhsNetworkUrl && <NFHSButton href={school.nfhsNetworkUrl}>Watch Live Games on</NFHSButton>}
                    {school.gofanTicketingUrl && <GoFanButton href={school.gofanTicketingUrl} />}
                  </>
                }
              />
            )}
            {photoGalleries.length > 0 && (
              <PhotosCalloutModule
                title="Pro Photo Galleries"
                right={<ApplicationShareButton path={school.webPhotosPath} title={`${school.name} Photos`} />}
                topText={`Check out photos of the ${school.mascot} in action from the SBLive Professional Photography Network!`}
                photoGalleries={photoGalleries}
                footer={
                  <Button variant={ButtonVariant.GRAY} width={ButtonWidth.FULL} size={ButtonSize.LARGE} href={school.webPhotosPath}>
                    See All {school.name} Galleries
                  </Button>
                }
              />
            )}
            <MinuteMediaAd placement="SIDEBAR_2" />
            {isSmallScreen && (
              <div style={{ width: '100%' }}>
                <MinuteMediaAd placement="IN_FEED" />
              </div>
            )}
            {articles.length > 0 && schoolInfo}
            {isSmallScreen && (
              <div style={{ width: '100%' }}>
                <MinuteMediaAd placement="IN_FEED_2" />
              </div>
            )}
            {school.popularTeams.nodes.length > 0 && (
              <ContentSection>
                <ContentSectionHeader
                  right={school.webTeamsPath && <ContentSectionHeaderLink href={school.webTeamsPath}>All Teams</ContentSectionHeaderLink>}
                  size={SectionHeaderSize.LARGE}
                >
                  Popular Teams
                </ContentSectionHeader>
                <ContentSectionFlushContent>
                  <Table
                    className={styles.PopularTeamsTable}
                    header={
                      <>
                        <TableHeaderCell>#</TableHeaderCell>
                        <TableHeaderCell>Teams</TableHeaderCell>
                        <TableHeaderCell align="right">Followers</TableHeaderCell>
                      </>
                    }
                  >
                    {school.popularTeams.nodes.map((team, index) => (
                      <TableLinkRow key={team.id} href={team.webPath}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <TextLink href={team.webPath} size={TextLinkSize.SMALL}>
                            <GenderSportIcon genderSport={team.genderSport} />
                            {team.genderSport.name}
                            {team.level.abbrev !== 'V' && ` (${team.level.abbrev})`}
                          </TextLink>
                        </TableCell>
                        <TableCell align="right">{team.fansCount}</TableCell>
                      </TableLinkRow>
                    ))}
                  </Table>
                </ContentSectionFlushContent>
                {school.webTeamsPath && <ContentSectionFooterButton href={school.webTeamsPath}>All Teams</ContentSectionFooterButton>}
              </ContentSection>
            )}
          </>
        }
      >
        {(articles.length > 0 || photoGalleries.length > 0) && (
          <ContentSection>
            <ContentSectionHeader
              size={SectionHeaderSize.LARGE}
              right={
                school.webNewsPath && (
                  <ContentSectionHeaderLink forward size={TextLinkSize.LARGE} href={school.webNewsPath}>
                    All News
                  </ContentSectionHeaderLink>
                )
              }
            >
              News & Updates
            </ContentSectionHeader>
            <NewsFeed articles={articles} photoGalleries={photoGalleries} defaultAuthorImage={school.image} />
          </ContentSection>
        )}
        {articles.length === 0 && (
          <>
            {schoolInfo}
            <StnVideoAd placement="INLINE" />
          </>
        )}
        <OutbrainAd />
      </PageLayout>
    </Page>
  );
});
